import BottomBar from 'components/AugmentedReality/Experience/BottomBar';
import {
  SCButtonIcon,
  SCExperienceContainer,
  SCExperienceStatus,
  SCExperienceStatusIcon,
  SCExperienceStatusLabel,
  SCFiltersButton,
  SCFiltersContainer,
  SCOverlayImage,
  SCVideo,
} from 'components/AugmentedReality/Experience/styled';
import useCamera from 'components/AugmentedReality/hooks/useCamera';
import TopBarCollapsible from 'components/AugmentedReality/Structure/TopBarCollapsible';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import expandImg from '../../../../public/static/ra/expand.svg';
import reduceImg from '../../../../public/static/ra/reduce.svg';
import { AugmentedRealityType } from '../../../types/augmentedReality';
import { useSwipe } from '../hooks/useSwipe';

interface ExperienceFrameProps {
  ra: AugmentedRealityType
  camera: ReturnType<typeof useCamera>
  download: boolean
  onDownload: (state: boolean) => void
  onPrevious: () => void
  onCancel: () => void
  onPhoto: () => void
  currentFilter: number
  onFilterChange: (filter: number) => void
  overlayTransform: { x: number; y: number; scale: number }
  setVisited: (visited: boolean) => void
  isPwa: boolean
}

const ExperienceFrame = ({ ra, camera, download, onDownload, onPrevious, onCancel, onPhoto, currentFilter, onFilterChange, overlayTransform, setVisited, isPwa = false }: ExperienceFrameProps) => {
  const { t } = useTranslation();
  const defaultIndex = ra.config.defaultFilter;
  const [uiVisible, setUiVisible] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [animatingIndex, setAnimatingIndex] = useState<number | null>(null);
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const filtersContainerRef = useRef<HTMLDivElement | null>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [dragOffset, setDragOffset] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!isPwa) {
      localStorage.setItem(`${ra.id}-visited`, 'true')
      setVisited(true)
    }
  }, [])

  const centerButton = (index: number, behavior: ScrollBehavior = 'smooth') => {
    const container = filtersContainerRef.current
    const button = buttonRefs.current[index]
    if (!container || !button) return
    const left = button.offsetLeft - (container.clientWidth - button.clientWidth) / 2
    container.scrollTo({ left, behavior })
  }

  useEffect(() => {
    onFilterChange(currentSlide)
    setAnimatingIndex(currentSlide)
    setTimeout(() => setAnimatingIndex(null), 750)
    centerButton(currentSlide)
  }, [currentSlide])

  const handleNext = () => {
    if (currentSlide < ra.experiences.length - 1) {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    }
  };

  const { onTouchMove, onTouchStart, onTouchEnd } = useSwipe({
    onScroll: setDragOffset,
    onSwipeLeft: handleNext,
    onSwipeRight: handlePrev,
  });

  useEffect(() => {
    if (videoRef.current && camera.stream) {
      videoRef.current.srcObject = camera.stream
    }
  }, [camera.stream])

  useEffect(() => {
    centerButton(defaultIndex)
  }, [defaultIndex])

  return (
    <SCExperienceContainer>
      {uiVisible && <TopBarCollapsible label={ra.experiences[currentFilter].text} />}

      {!isPaused ? (
        <>
          <SCVideo ref={videoRef} autoPlay playsInline muted/>
        </>
      ) : (
        <>
          <SCVideo as="img" src={snapshotUrl} alt="Paused view" />
        </>
      )}

      <SCFiltersContainer ref={filtersContainerRef} onTouchMove={onTouchMove} style={{ transform: `translateX(${-dragOffset}px)` }}>
        {ra.experiences?.map((experience, index) => {
          const isSelected = currentFilter === index
          return (
            <SCFiltersButton
              key={index}
              ref={(el) => void (buttonRefs.current[index] = el)}
              onClick={() => {
                setCurrentSlide(index);
              }}
              $isSelected={isSelected}
              className={animatingIndex === index ? 'animate' : ''}
            >
              {experience.name}
            </SCFiltersButton>
          )
        })}
      </SCFiltersContainer>
      {!isPaused && (
        <SCExperienceStatus>
          <SCExperienceStatusIcon />{' '}
          <SCExperienceStatusLabel>{t('augmentedReality.experience.live')}</SCExperienceStatusLabel>
        </SCExperienceStatus>
      )}

      <div
        style={{
          position: 'absolute',
          inset: 0,
          transform: `translate(${overlayTransform.x}px, ${overlayTransform.y}px) scale(${overlayTransform.scale})`,
          transformOrigin: '0px 0px',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      >
        {ra.experiences.map((experience, index) => (
          <SCOverlayImage
            key={experience.image}
            crossOrigin="anonymous"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            src={experience.image}
            alt="filter overlay"
            sizes="100vw"
            style={{
              opacity: currentFilter === index ? 1 : 0, pointerEvents: 'auto',
              cursor: 'pointer'
            }}
            $size={300}
          />
        ))}
      </div>

      {uiVisible ? (
        <SCButtonIcon src={expandImg.src} onClick={() => setUiVisible(false)} />
      ) : (
        <SCButtonIcon src={reduceImg.src} onClick={() => setUiVisible(true)} />
      )}

      {uiVisible && (
        <BottomBar
          ra={ra}
          camera={camera}
          videoRef={videoRef}
          download={download}
          onDownload={onDownload}
          currentOverlay={ra.experiences[currentFilter].image}
          onPrevious={onPrevious}
          onCancel={onCancel}
          isPaused={isPaused}
          onPaused={setIsPaused}
          snapshot={snapshotUrl}
          setSnapshot={setSnapshotUrl}
          onPhoto={onPhoto}
        />
      )}
    </SCExperienceContainer>
  )
}

export default ExperienceFrame;
